/*  MUI  */
import { Box, TableCell, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { RiskPicker } from 'application/Shared/Components/Pickers';
import { SSOSupportIcons } from 'application/Shared/Components/SSOSupportIcons';
import { ManagedTable } from 'application/Shared/Components/Table';
import { HMS } from 'application/Shared/Components/Usage';
import { Helpers, SSOUtils } from 'application/Shared/Helpers';

/*  Objects  */
import { Application } from 'objects/Application';

/*  React  */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparklines, SparklinesBars, SparklinesLine } from 'react-sparklines';

/*  Constants  */
const { padArray } = Helpers;

/*  Constants  */
const headers = (t) => [
    {
        align: 'left',
        field: 'riubApplication',
        renderHeader: t('Common.application'),
        width: '20%'
    },
    {
        align: 'left',
        field: 'ssoSupport',
        renderHeader: t('Common.ssoCoverage'),
        width: '10%'
    },
    {
        align: 'left',
        field: 'risk',
        renderHeader: t('Common.risk'),
        width: '10%'
    },
    {
        align: 'left',
        field: 'userCount',
        renderHeader: t('Common.users'),
        width: '10%'
    },
    {
        align: 'left',
        field: 'usage',
        renderHeader: t('Common.usage'),
        width: '10%'
    },
    {
        align: 'left',
        field: 'usageChart',
        renderHeader: t('Common.usage_chart'),
        width: '10%'
    }
];

export const QBRManagedTable = ({ rows, rowsPerPage, title }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const localRows = useMemo(
        () =>
            rows.map((row) => ({
                ...row,
                ssoSupport: SSOUtils.tenantAppGetSSOSupport(row)
            })),
        [rows]
    );

    const tableHeaders = useMemo(() => headers(t), [t]);

    return (
        <Box style={{ marginLeft: 0, marginRight: 0 }}>
            <Typography variant="h5" style={{ marginBottom: '8px' }}>
                {title}
            </Typography>
            <ManagedTable
                headers={tableHeaders}
                renderBody={(row) => (
                    <>
                        <TableCell>
                            <Application data={row.application} nameHasLink>
                                <ApplicationNameLinkCell
                                    nameNoTruncate
                                    withCategory
                                />
                            </Application>
                        </TableCell>
                        <TableCell>
                            <SSOSupportIcons
                                ssoSupport={row.ssoSupport}
                                justify="left"
                                enableTooltip
                                tenantApp={row}
                            />
                        </TableCell>
                        <TableCell>
                            {row.application.risk ? (
                                <RiskPicker
                                    visualOnly
                                    value={row.application.risk}
                                />
                            ) : (
                                <span />
                            )}
                        </TableCell>
                        <TableCell>
                            {row.users ? row.users.length : 0}
                        </TableCell>
                        <TableCell>
                            <HMS seconds={row.usage} />
                        </TableCell>
                        <TableCell>
                            <Box width="100px">
                                <Sparklines
                                    data={padArray(row.usageChart, 20, 0)}
                                >
                                    <SparklinesBars
                                        style={{
                                            fill: theme.palette.sparkLines
                                                .sparkBar,
                                            stroke: theme.palette.sparkLines
                                                .stroke
                                        }}
                                    />
                                    <SparklinesLine
                                        color={
                                            theme.palette.sparkLines.sparkLine
                                        }
                                        style={{
                                            fill: 'none'
                                        }}
                                    />
                                </Sparklines>
                            </Box>
                        </TableCell>
                    </>
                )}
                renderBodyKey={null}
                renderFooter={false}
                rows={localRows}
                rowsPerPage={rowsPerPage} // -1 or 10
                searchEnabled={false}
                sortDirection="DESC"
                sortFieldType="number"
                sortKey="usage"
                width="100%"
            />
        </Box>
    );
};
